import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Site from "../../model/Site";
import { useApiClient } from "../../contexts/ApiClientContext";
import Loading from "../common/Loading";
import { useAuth } from "../../contexts/AuthContext";
import FormInputText from "../common/FormInputText";
import FormTextArea from "../common/FormTextArea";
import FormContainer from "../common/FormContainer";
import SETTINGS from "../../globals";
import { IApiClientError } from "../../shared/ApiClient";
import APIEndPoints from "../../utils/APIEndPoints";
import APIError from "../../utils/APIError";
import { FaInfoCircle } from "react-icons/fa";
import { useAlerts } from "../../contexts/AlertsContext";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import DialogWindowType from "../common/dialog_window/DialogWindowType";
import UploadManager from "../../shared/UploadManager";
import FormInputImage from "../common/FormInputImage";
import ImgUtil from "../../utils/ImgUtil";
import UploadFileType from "../../model/enums/UploadFileType";
import AppSubSections from "../../utils/AppSubSections";
import { FaTriangleExclamation } from "react-icons/fa6";
import { EXPLANATION_HEADER } from "../common/dialog_window/LicenseDialog";
import AppSections from "../../utils/AppSections";

interface ISiteFormErrors {
  name: string;
  description: string;
}

const defaultFormErrors: ISiteFormErrors = {
  name: "",
  description: "",
};

function SiteAddEdit() {
  // read the ID from the url
  const { id } = useParams();

  // manage loading animation
  const [loading, setLoading] = useState(true);
  // set the state to track any error messages received from the server
  const [errorMessage, setErrorMessage] = useState("");
  // set state for form info
  const [formData, setFormData] = useState<Site>(Site.EmptyInstance());
  // set state for form errors
  const [formErrors, setFormErrors] =
    useState<ISiteFormErrors>(defaultFormErrors);

  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the AuthManager
  const authManager = useAuth();
  // reference to the alerts system
  const alertsManager = useAlerts();
  // instance of UploadManager
  const [uploadManager] = useState(new UploadManager({ apiClient: apiClient }));
  // flag to display/hide the authorization message
  const [userIsAuthorised, setUserIsAuthorised] = useState(false);

  const [agreeTerms, setAgreeTerms] = useState<boolean>(false);

  // load record data from the server
  useEffect(() => {
    const getAllData = async () => {
      try {
        let site: Site | undefined = undefined;
        if (id) {
          site = await apiClient?.get<Site>({
            url: APIEndPoints.SITES.ONE(parseInt(id)),
          });
        }

        // set into the state all the information
        setFormData(site ? site : Site.EmptyInstance());
      } catch (error: any) {
        if (!error.processed) {
          setErrorMessage("Something went wrong. Try again later!");
        }
      }
      setLoading(false);
    };
    getAllData();
  }, [apiClient, id]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // get the errors from the form
    const errors = validateSiteForm(formData, "Site");

    if (isFormInvalid(errors)) {
      setFormErrors(errors);
      setErrorMessage(
        "Some of the data input is invalid. Please check and try again.",
      );
      return;
    }

    var iconServerId: number | undefined = id ? formData.icon : undefined;
    try {
      // upload the new icon, if it exists (situation managed by the UploadManager)
      const uploadResult = await uploadManager.uploadFiles();
      console.log(uploadResult);
      if (uploadResult instanceof Array) {
        iconServerId = uploadResult[0].serverId;
      }
    } catch (exception) {
      console.log(exception);
    }

    var body = new Site(
      authManager.getUserID()!!,
      formData.id_pilot,
      formData.id_country,
      formData.name,
      formData.description ? formData.description : undefined,
      iconServerId,
      formData.latitude,
      formData.longitude,
    );

    // check if we have EDIT or CREATE
    if (id) {
      body.id = parseInt(id);
      apiClient
        ?.put({
          url: APIEndPoints.SITES.ONE(parseInt(id)),
          body: body,
        })
        .then((response) => {
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "Site updated successfully",
          });
          navigate(
            SETTINGS.get_path({
              section: AppSections.SITES,
              id: parseInt(id),
              subsection: AppSubSections.VIEW,
            }),
          );
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            processAPIErrors(error);
          }
        });
    } else {
      apiClient
        ?.post<Site>({ url: APIEndPoints.SITES.NEW, body: body })
        .then((response) => {
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "Site created successfully",
          });
          navigate(
            SETTINGS.get_path({
              section: AppSections.SITES,
              id: response.id,
              subsection: AppSubSections.VIEW,
            }),
          );
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            processAPIErrors(error);
          }
        });
    }
  };

  const handleDelete = () => {
    apiClient
      ?.delete({
        url: APIEndPoints.SITES.ONE(parseInt(id!)),
      })
      .then((response) => {
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "Site deleted successfully",
        });
        navigate(SETTINGS.get_path({ section: AppSections.SITES }));
      })
      .catch((error: IApiClientError) => {
        if (!error.processed) {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Unable to delete this site",
          });
        }
      });
  };

  const formInputChange = (inputID: string, newValue: string) => {
    const formDataCopy = JSON.parse(JSON.stringify(formData));
    formDataCopy[inputID] = newValue;
    setFormData(formDataCopy as Site);
  };

  const formIconChange = (
    id: string,
    value: string | undefined,
    files: FileList | null,
  ) => {
    const formDataCopy = JSON.parse(JSON.stringify(formData));
    formDataCopy["icon_path"] = value;
    formDataCopy["icon"] = -1;
    uploadManager.replaceFiles(id, UploadFileType.ICON, files);
    setFormData(formDataCopy as Site);
  };

  const onFormCancel = () => {
    navigate(SETTINGS.go_back({ section: AppSections.SITES }));
  };

  const processAPIErrors = (serverError: IApiClientError) => {
    try {
      // convert the error to ChemiNovaAPI custom errors
      const apiErrors = serverError.details.response.data as APIError[];
      const formErrorsCopy: any = defaultFormErrors;
      console.log(serverError);
      console.log(apiErrors);
      apiErrors.forEach((apiError) => {
        formErrorsCopy[apiError.loc[0]] = apiError.msg;
      });
      setFormErrors(formErrorsCopy);
      setErrorMessage(
        "Some of the data input is invalid. Please check and try again.",
      );
    } catch (exception) {
      console.log(exception);
      setErrorMessage("Something went wrong. Please try again later!");
    }
  };

  const pageTitle = id ? "Edit site" : "Create a new site";
  const confirmLabel = id ? "Update site" : "Create site";

  const deleteButton = id ? (
    <Button
      label={"Delete site"}
      purpose={ButtonAction.DELETE}
      onClick={() => {
        alertsManager.showDialogWindow({
          type: DialogWindowType.CONFIRM,
          message: "Are you sure you want to delete this site ?",
          cancelLabel: "Cancel",
          confirmLabel: "Confirm",
          onConfirm: handleDelete,
        });
      }}
    />
  ) : null;

  if (loading) {
    return <Loading />;
  }

  return (
    <FormContainer
      title={pageTitle}
      onSubmit={(e) => {
        handleSubmit(e);
      }}
      headButton={deleteButton}
      hint="sites"
    >
      {!id && !userIsAuthorised && (
        <div>
          <div className="text-4xl mt-46 mb-20 text-center">
            <FaTriangleExclamation className="inline-block text-red-600 mr-10 mb-2" />
            <span>IMPORTANT</span>
            <FaTriangleExclamation className="inline-block text-red-600 ml-10 mb-2" />
          </div>
          <div className="text-2xl">
            The record you are about to create will be public ! <br />
            <br />
            Please confirm that <strong>you are the responsible</strong> or{" "}
            <strong>have the approval of the responsible</strong> of the Site
            you are about to create, to publish this information.
          </div>
          <div className="form-buttons-container mt-20">
            <Button
              label="Cancel"
              purpose={ButtonAction.CANCEL}
              onClick={() => onFormCancel()}
              className="w-full"
            />
            <Button
              label="I am authorised to create the record"
              purpose={ButtonAction.CHECKED_YES}
              className="w-full"
              onClick={() => setUserIsAuthorised(true)}
            />
          </div>
        </div>
      )}
      {(userIsAuthorised || id) && (
        <>
          <FormInputImage
            label="Picture"
            id="icon"
            placeholderImage=""
            value={ImgUtil.display_icon(formData.icon_path)}
            required={false}
            onChange={formIconChange}
          />

          <FormInputText
            label={"Site name"}
            id="name"
            value={formData.name}
            required={true}
            placeholder="Schönbrunn Palace"
            onChange={formInputChange}
            error={formErrors.name}
          />

          <FormTextArea
            label="Description"
            id="description"
            value={formData.description}
            required={false}
            onChange={formInputChange}
            error={formErrors.description}
          />

          {errorMessage && (
            <div className="form-submit-error">
              <FaInfoCircle />
              <div>{errorMessage}</div>
            </div>
          )}

          {/* License Agreement group */}
          <div className="form-input-group mt-4 flex flex-col gap-2 pl-4">
            <label className="text-sm">{EXPLANATION_HEADER}</label>

            <label className="flex items-center gap-2 text-sm mt-1">
              <input
                type="checkbox"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
              />
              <span>
                I agree with the{" "}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    alertsManager.showDialogWindow({
                      type: DialogWindowType.LICENSE,
                      message: "license",
                    });
                  }}
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  Terms and Conditions
                </a>
              </span>
            </label>
          </div>

          <div className="form-buttons-container">
            <Button
              label="Cancel"
              purpose={ButtonAction.CANCEL}
              onClick={() => onFormCancel()}
              className="w-full"
            />
            <Button
              label={confirmLabel}
              purpose={ButtonAction.SAVE}
              isSubmit={true}
              disabled={!agreeTerms}
              className="w-full"
            />
          </div>
        </>
      )}
    </FormContainer>
  );
}

function validateSiteForm(
  formData: Site,
  groupingLabeL: string,
): ISiteFormErrors {
  let errors: ISiteFormErrors = {
    name: "",
    description: "",
  };

  errors.name =
    formData.name.length < 4
      ? groupingLabeL + " name should be at least 4 characters long"
      : "";
  /*errors.description =
    formData.description.length < 10
      ? groupingLabeL + " description should be at least 10 characters long"
      : "";*/

  return errors;
}

function isFormInvalid(errors: ISiteFormErrors): boolean {
  return errors.name !== "" || errors.description !== "";
}

export default SiteAddEdit;
